import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const STEPS = [
    { id: 1, title: 'Upload Plan', emoji: '📁' },
    { id: 2, title: 'Orientation', emoji: '🧭' },
    { id: 3, title: 'Center Point', emoji: '🎯' },
    { id: 4, title: 'Verify Rooms', emoji: '🏷️' },
    { id: 5, title: 'Result', emoji: '📜' },
];

const ROOM_TEMPLATES = [
    { label: 'Kitchen', icon: '🍳', defaultPos: { x: 75, y: 75 }, category: 'SE' },
    { label: 'Master Bedroom', icon: '🛏️', defaultPos: { x: 25, y: 75 }, category: 'SW' },
    { label: 'Living Room', icon: '🛋️', defaultPos: { x: 25, y: 25 }, category: 'NE' },
    { label: 'Toilet', icon: '🚿', defaultPos: { x: 75, y: 25 }, category: 'NW' },
];

const VastuCalculatorAI: React.FC = () => {
    const [currentStep, setCurrentStep] = useState(1);
    const [image, setImage] = useState<string | null>(null);
    const [rotation, setRotation] = useState(0);
    const [center, setCenter] = useState({ x: 50, y: 50 });
    const [isScanning, setIsScanning] = useState(false);
    const [rooms, setRooms] = useState<any[]>([]);

    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (event) => {
                setImage(event.target?.result as string);
                setCurrentStep(2);
            };
            reader.readAsDataURL(file);
        }
    };

    const startScanning = () => {
        setIsScanning(true);
        setTimeout(() => {
            setRooms(ROOM_TEMPLATES);
            setIsScanning(false);
            setCurrentStep(4);
        }, 2000);
    };

    const calculateScore = () => {
        // Mock score calculation logic based on rotation and positions
        return 82; 
    };

    return (
        <div className="bg-white rounded-[2rem] purple-card-shadow border border-slate-100 overflow-hidden min-h-[600px] flex flex-col md:flex-row">
            {/* Left Sidebar - Stepper */}
            <div className="w-full md:w-64 bg-slate-50 border-b md:border-b-0 md:border-r-2 border-black p-6 flex md:flex-col gap-4 overflow-x-auto no-scrollbar">
                <div className="hidden md:block mb-8">
                    <h3 className="text-xl font-black text-[#05144b] uppercase italic leading-none">AI Vastu</h3>
                    <p className="text-[10px] font-bold text-black opacity-40 uppercase tracking-widest mt-1">Analysis Engine</p>
                </div>
                {STEPS.map((step) => (
                    <div 
                        key={step.id}
                        className={`flex items-center gap-4 transition-all shrink-0 ${currentStep === step.id ? 'opacity-100 scale-105' : 'opacity-30'}`}
                    >
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-lg border-2 ${currentStep === step.id ? 'bg-white border-black shadow-lg' : 'bg-white/50 border-black/10'}`}>
                            {currentStep > step.id ? '✅' : step.emoji}
                        </div>
                        <div className="hidden md:block">
                            <p className="text-[11px] font-black uppercase text-[#05144b] leading-none mb-0.5">{step.title}</p>
                            <p className="text-[8px] font-bold text-black opacity-40 uppercase tracking-tighter">Step 0{step.id}</p>
                        </div>
                    </div>
                ))}
            </div>

            {/* Main Content Area */}
            <div className="flex-1 p-6 md:p-10 flex flex-col relative overflow-hidden">
                <AnimatePresence mode="wait">
                    {/* Step 1: Upload */}
                    {currentStep === 1 && (
                        <motion.div 
                            key="step1"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            className="flex-1 flex flex-col items-center justify-center text-center"
                        >
                            <div 
                                onClick={() => fileInputRef.current?.click()}
                                className="w-full max-w-md border-4 border-dashed border-black/10 rounded-[2.5rem] p-12 hover:border-[#f1ae24] hover:bg-[#f1ae24]/5 transition-all cursor-pointer group"
                            >
                                <div className="w-20 h-20 bg-black text-white rounded-3xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform shadow-xl">
                                    <i className="fa-solid fa-cloud-arrow-up text-3xl"></i>
                                </div>
                                <h3 className="text-2xl font-black text-[#05144b] uppercase italic mb-2">Upload Floor Plan</h3>
                                <p className="text-sm font-bold text-black opacity-60">Drag and drop your property layout or click to browse (JPG, PNG)</p>
                                <input 
                                    type="file" 
                                    ref={fileInputRef} 
                                    onChange={handleFileUpload} 
                                    className="hidden" 
                                    accept="image/*"
                                />
                            </div>
                        </motion.div>
                    )}

                    {/* Step 2: Orientation */}
                    {currentStep === 2 && (
                        <motion.div 
                            key="step2"
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="flex-1 flex flex-col"
                        >
                            <div className="mb-6">
                                <h2 className="text-2xl font-black text-[#05144b] uppercase italic">Set Orientation</h2>
                                <p className="text-sm font-bold text-black opacity-60">Rotate the compass to align with the actual North of your property.</p>
                            </div>
                            
                            <div className="flex-1 relative bg-slate-100 rounded-3xl overflow-hidden border-2 border-black/5 flex items-center justify-center">
                                {image && <img src={image} alt="Plan" className="max-h-[400px] object-contain opacity-50" />}
                                
                                {/* Compass Overlay */}
                                <motion.div 
                                    style={{ rotate: rotation }}
                                    className="absolute w-64 h-64 border-2 border-black/20 rounded-full flex items-center justify-center pointer-events-none"
                                >
                                    <div className="absolute top-0 w-1 h-8 bg-red-600 rounded-full shadow-lg"></div>
                                    <div className="absolute top-[-25px] font-black text-red-600 text-sm">NORTH</div>
                                    <div className="w-full h-[1px] bg-black/10 absolute"></div>
                                    <div className="h-full w-[1px] bg-black/10 absolute"></div>
                                    <div className="w-16 h-16 bg-white border-2 border-black rounded-full flex items-center justify-center shadow-2xl">
                                        <i className="fa-solid fa-compass text-2xl text-black"></i>
                                    </div>
                                </motion.div>

                                {/* Rotation Slider */}
                                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-white border-2 border-black px-6 py-3 rounded-full flex items-center gap-4 shadow-2xl">
                                    <span className="text-[10px] font-black uppercase text-black">Rotate</span>
                                    <input 
                                        type="range" 
                                        min="0" max="360" 
                                        value={rotation} 
                                        onChange={(e) => setRotation(parseInt(e.target.value))}
                                        className="w-48 accent-black"
                                    />
                                    <span className="text-[10px] font-black w-8 text-black">{rotation}°</span>
                                </div>
                            </div>

                            <div className="mt-8 flex justify-end gap-4">
                                <button onClick={() => setCurrentStep(1)} className="px-8 py-3 font-black uppercase text-xs opacity-40 hover:opacity-100 text-black">Back</button>
                                <button 
                                    onClick={() => setCurrentStep(3)} 
                                    className="px-10 py-4 bg-black text-white rounded-full font-black uppercase tracking-widest text-xs hover:bg-[#f1ae24] transition-all shadow-xl"
                                >
                                    Confirm Orientation
                                </button>
                            </div>
                        </motion.div>
                    )}

                    {/* Step 3: Center Point */}
                    {currentStep === 3 && (
                        <motion.div 
                            key="step3"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="flex-1 flex flex-col"
                        >
                            <div className="mb-6">
                                <h2 className="text-2xl font-black text-[#05144b] uppercase italic">Mark Center Point</h2>
                                <p className="text-sm font-bold text-black opacity-60">Click on the image to set the geometric center (Brahmasthan) of your property.</p>
                            </div>
                            
                            <div 
                                className="flex-1 relative bg-slate-100 rounded-3xl overflow-hidden border-2 border-black/5 flex items-center justify-center cursor-crosshair"
                                onClick={(e) => {
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    const x = ((e.clientX - rect.left) / rect.width) * 100;
                                    const y = ((e.clientY - rect.top) / rect.height) * 100;
                                    setCenter({ x, y });
                                }}
                            >
                                {image && <img src={image} alt="Plan" className="max-h-[400px] object-contain" />}
                                
                                {/* Center Marker */}
                                <motion.div 
                                    animate={{ left: `${center.x}%`, top: `${center.y}%` }}
                                    className="absolute w-12 h-12 -ml-6 -mt-6 border-4 border-[#f1ae24] rounded-full flex items-center justify-center bg-[#f1ae24]/10 backdrop-blur-sm pointer-events-none shadow-2xl"
                                >
                                    <div className="w-2 h-2 bg-black rounded-full"></div>
                                    <div className="absolute w-full h-[1px] bg-black/20"></div>
                                    <div className="absolute h-full w-[1px] bg-black/20"></div>
                                    <span className="absolute -bottom-6 text-[8px] font-black uppercase bg-black text-white px-2 py-0.5 rounded-full">Brahmasthan</span>
                                </motion.div>
                            </div>

                            <div className="mt-8 flex justify-end gap-4">
                                <button onClick={() => setCurrentStep(2)} className="px-8 py-3 font-black uppercase text-xs opacity-40 hover:opacity-100 text-black">Back</button>
                                <button 
                                    onClick={startScanning} 
                                    className={`px-10 py-4 bg-black text-white rounded-full font-black uppercase tracking-widest text-xs hover:bg-[#f1ae24] transition-all shadow-xl flex items-center gap-3 ${isScanning ? 'opacity-70 pointer-events-none' : ''}`}
                                >
                                    {isScanning ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                            AI Scanning...
                                        </>
                                    ) : 'Analyze Room Zones'}
                                </button>
                            </div>
                        </motion.div>
                    )}

                    {/* Step 4: Verify Rooms */}
                    {currentStep === 4 && (
                        <motion.div 
                            key="step4"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="flex-1 flex flex-col"
                        >
                            <div className="mb-6">
                                <h2 className="text-2xl font-black text-[#05144b] uppercase italic">AI Detected Rooms</h2>
                                <p className="text-sm font-bold text-black opacity-60">We've identified your rooms. Drag them to adjust their exact positions if needed.</p>
                            </div>
                            
                            <div className="flex-1 relative bg-slate-100 rounded-3xl overflow-hidden border-2 border-black/5 flex items-center justify-center">
                                {image && <img src={image} alt="Plan" className="max-h-[400px] object-contain opacity-80" />}
                                
                                {/* Detected Rooms */}
                                {rooms.map((room, i) => (
                                    <motion.div 
                                        key={i}
                                        drag
                                        dragMomentum={false}
                                        initial={{ left: `${room.defaultPos.x}%`, top: `${room.defaultPos.y}%` }}
                                        className="absolute p-3 bg-white border-2 border-black rounded-2xl shadow-xl flex items-center gap-3 cursor-grab active:cursor-grabbing"
                                    >
                                        <span className="text-xl">{room.icon}</span>
                                        <div className="text-left">
                                            <p className="text-[10px] font-black uppercase leading-none text-[#05144b]">{room.label}</p>
                                            <p className="text-[8px] font-bold text-black opacity-40 uppercase mt-1">Zone: {room.category}</p>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>

                            <div className="mt-8 flex justify-end gap-4">
                                <button onClick={() => setCurrentStep(3)} className="px-8 py-3 font-black uppercase text-xs opacity-40 hover:opacity-100 text-black">Back</button>
                                <button 
                                    onClick={() => setCurrentStep(5)} 
                                    className="px-10 py-4 bg-black text-white rounded-full font-black uppercase tracking-widest text-xs hover:bg-[#f1ae24] transition-all shadow-xl"
                                >
                                    Generate Vastu Report
                                </button>
                            </div>
                        </motion.div>
                    )}

                    {/* Step 5: Result */}
                    {currentStep === 5 && (
                        <motion.div 
                            key="step5"
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="flex-1 flex flex-col items-center justify-center text-center"
                        >
                            <div className="relative mb-8">
                                <div className="w-48 h-48 rounded-full border-[12px] border-black flex flex-col items-center justify-center bg-white shadow-2xl relative z-10">
                                    <span className="text-7xl font-black text-[#05144b] italic leading-none">{calculateScore()}</span>
                                    <span className="text-[10px] font-black text-black uppercase tracking-[0.2em] mt-2">Score</span>
                                </div>
                                <motion.div 
                                    animate={{ scale: [1, 1.2, 1] }}
                                    transition={{ repeat: Infinity, duration: 2 }}
                                    className="absolute inset-0 border-4 border-[#f1ae24]/20 rounded-full"
                                ></motion.div>
                            </div>

                            <div className="max-w-md">
                                <div className="inline-flex items-center gap-2 px-6 py-2 bg-emerald-50 text-emerald-700 border-2 border-emerald-100 rounded-full text-xs font-black uppercase mb-4">
                                    <i className="fa-solid fa-circle-check"></i>
                                    Good Vastu Compliance
                                </div>
                                <h2 className="text-3xl font-black text-[#05144b] uppercase italic mb-4">Analysis Complete</h2>
                                <p className="text-sm font-bold text-black opacity-60 mb-10">Your property layout has been analyzed using AI directional mapping. We've identified 3 auspicious zones and 1 zone requiring minor remedies.</p>
                                
                                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                    <button 
                                        onClick={() => setCurrentStep(1)} 
                                        className="px-8 py-4 bg-white border-2 border-black text-black rounded-full font-black uppercase tracking-widest text-xs hover:bg-black hover:text-white transition-all shadow-lg"
                                    >
                                        New Analysis
                                    </button>
                                    <button 
                                        className="px-8 py-4 bg-[#f1ae24] text-black rounded-full font-black uppercase tracking-widest text-xs hover:scale-105 transition-all shadow-xl"
                                    >
                                        Download Report
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Background Text Decor */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 -z-10 pointer-events-none">
                    <span className="text-[15rem] font-black text-black/[0.02] uppercase leading-none italic select-none">VASTU</span>
                </div>
            </div>
        </div>
    );
};

export default VastuCalculatorAI;
